import { LayoutDashboard, Inbox, FolderOpen } from 'lucide-react';

interface BrandingPreviewPanelProps {
  primary: string;
  accent: string;
  gold: string;
  firmName: string;
  logoUrl: string | null;
  headingFont: string;
  bodyFont: string;
  borderRadius: string;
  sidebarStyle?: 'light' | 'tinted' | 'dark';
}

interface PreviewSidebarColors {
  bg: string;
  text: string;
  textMuted: string;
  activeBg: string;
  logoText: string;
}

function resolvePreviewSidebarColors(
  primary: string,
  sidebarStyle: 'light' | 'tinted' | 'dark',
): PreviewSidebarColors {
  switch (sidebarStyle) {
    case 'dark':
      return {
        bg: primary,
        text: '#ffffff',
        textMuted: 'rgba(255,255,255,0.65)',
        activeBg: 'rgba(255,255,255,0.12)',
        logoText: '#ffffff',
      };
    case 'tinted':
      // Use a simple alpha blend approximation for preview (color-mix not reliable in inline styles)
      return {
        bg: `color-mix(in srgb, ${primary} 8%, #ffffff)`,
        text: '#1a1a1a',
        textMuted: '#5a6b63',
        activeBg: `color-mix(in srgb, ${primary} 18%, #ffffff)`,
        logoText: primary,
      };
    case 'light':
    default:
      return {
        bg: '#ffffff',
        text: '#1a1a1a',
        textMuted: '#9ca3af',
        activeBg: `${primary}14`,
        logoText: primary,
      };
  }
}

export function BrandingPreviewPanel({
  primary,
  accent,
  gold,
  firmName,
  logoUrl,
  headingFont,
  bodyFont,
  borderRadius,
  sidebarStyle = 'light',
}: BrandingPreviewPanelProps) {
  const sidebar = resolvePreviewSidebarColors(primary, sidebarStyle);
  const cardBg = '#ffffff';
  const radiusPx = borderRadius;

  return (
    <div className="sticky top-6 space-y-3">
      <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">
        Aperçu en direct
      </p>

      <div
        className="border border-neutral-200 rounded-xl overflow-hidden shadow-sm"
        style={{ fontFamily: `'${bodyFont}', sans-serif` }}
        aria-label="Aperçu de l'interface avec les couleurs sélectionnées"
      >
        <div className="flex h-64">
          {/* Mini sidebar */}
          <div
            className="w-28 shrink-0 flex flex-col p-2 gap-1"
            style={{ backgroundColor: sidebar.bg }}
          >
            {/* Logo / firm name */}
            <div className="flex items-center gap-1.5 px-1 py-2 mb-1">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="h-5 w-5 object-contain rounded"
                />
              ) : (
                <div
                  className="w-5 h-5 rounded flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: gold, color: sidebar.bg }}
                >
                  {firmName.charAt(0).toUpperCase()}
                </div>
              )}
              <span
                className="text-xs font-semibold truncate"
                style={{
                  color: sidebar.logoText,
                  fontFamily: `'${headingFont}', Georgia, serif`,
                }}
              >
                {firmName || 'CIME'}
              </span>
            </div>

            {/* Nav items */}
            {[
              { Icon: LayoutDashboard, label: 'Tableau de bord', active: false },
              { Icon: Inbox, label: 'Demandes', active: true },
              { Icon: FolderOpen, label: 'Dossiers', active: false },
            ].map(({ Icon, label, active }) => (
              <div
                key={label}
                className="flex items-center gap-1.5 px-1.5 py-1 rounded transition-colors"
                style={{
                  backgroundColor: active ? sidebar.activeBg : 'transparent',
                }}
              >
                <Icon
                  className="w-3 h-3 shrink-0"
                  style={{ color: active ? sidebar.text : sidebar.textMuted }}
                  aria-hidden
                />
                <span
                  className="text-xs truncate"
                  style={{ color: active ? sidebar.text : sidebar.textMuted }}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* Main content area */}
          <div className="flex-1 bg-neutral-50 p-3 space-y-2 overflow-hidden">
            {/* Mock ticket card */}
            <div
              className="p-2.5 shadow-sm border border-neutral-200"
              style={{ backgroundColor: cardBg, borderRadius: radiusPx }}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <p
                  className="text-xs font-semibold text-neutral-900 leading-tight"
                  style={{ fontFamily: `'${headingFont}', Georgia, serif` }}
                >
                  Demande de consultation
                </p>
                {/* Gold badge */}
                <span
                  className="text-xs px-1.5 py-0.5 rounded font-medium shrink-0"
                  style={{
                    backgroundColor: `${gold}20`,
                    color: gold,
                    borderRadius: '4px',
                  }}
                >
                  Urgent
                </span>
              </div>
              <p className="text-xs text-neutral-500 mb-2 line-clamp-1">
                Client demande un rendez-vous pour un litige commercial…
              </p>
              {/* Primary button */}
              <button
                type="button"
                className="px-2 py-1 text-xs font-medium text-white"
                style={{
                  backgroundColor: primary,
                  borderRadius: radiusPx,
                }}
              >
                Répondre
              </button>
            </div>

            {/* Second mock card (lighter) */}
            <div
              className="p-2 border border-neutral-100 bg-white/80"
              style={{ borderRadius: radiusPx }}
            >
              <div className="flex items-center gap-1.5">
                <div
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: accent }}
                />
                <p className="text-xs text-neutral-600 truncate">
                  Dossier Dupont c. SARL Martin
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Color swatches strip */}
        <div className="flex border-t border-neutral-100">
          {[
            { color: primary, label: 'Primaire' },
            { color: accent, label: 'Accent' },
            { color: gold, label: 'Or' },
          ].map(({ color, label }) => (
            <div
              key={label}
              className="flex-1 flex flex-col items-center gap-0.5 py-2"
              title={color}
            >
              <div
                className="w-5 h-5 rounded-full border border-white shadow-sm"
                style={{ backgroundColor: color }}
                aria-label={`Couleur ${label} : ${color}`}
              />
              <span className="text-xs text-neutral-400">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
